import api from './api';

/**
 * permissionRules — `IMPLIED_PERMISSIONS`, fetched from the Python definition.
 *
 * The rule that decides whether an approver can see the approval inbox used to
 * be written out twice — `apps/authorization/permissions.py` and
 * `frontend/src/hooks/usePermissions.js` — each with a comment telling the
 * reader to keep them in step. Two sources of truth for one rule, and a
 * divergence shows up as a sidebar entry the API refuses (or, worse, one it
 * would have allowed).
 *
 * **`usePermissions.hasPermission` is synchronous** — it is called during
 * render, hundreds of times per page — so this module holds the served rules in
 * a module-level variable and the hook reads whatever is there. Before the
 * fetch resolves, and forever if it fails, the hook falls back to its own
 * hardcoded copy. That is deliberate: a failed request must not silently
 * *remove* a menu entry the user is entitled to.
 *
 * Fetched ONCE per session (`primePermissionRules`, called at app start), for
 * the same reason `navigationRegistry` is: a per-render fetch would be a
 * request storm, and nothing here changes between two page loads.
 */

const LOAD_TIMEOUT_MS = 6000;

let served = null;
let inflight = null;

/** The served rule map, or `null` when it has not (yet) arrived. */
export const getImpliedRules = () => served;

/**
 * Fetch the rules once. Never rejects — the caller has a working fallback and
 * must not be made to handle an error it cannot act on.
 */
export const primePermissionRules = () => {
  if (served) return Promise.resolve(served);
  if (inflight) return inflight;

  inflight = api
    .get('/users/api/permission-rules/', { timeout: LOAD_TIMEOUT_MS })
    .then(({ data }) => {
      if (data && data.available && data.implied) served = data.implied;
      return served;
    })
    .catch(() => null)
    .then((result) => {
      inflight = null;
      return result;
    });

  return inflight;
};

/** Discard the cache — used on logout, so the next session refetches. */
export const resetPermissionRules = () => {
  served = null;
  inflight = null;
};

const permissionRules = { getImpliedRules, primePermissionRules, resetPermissionRules };

export default permissionRules;
